import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2 } from "lucide-react";

import {
  buildClubPostShareUrl,
  toggleClubPostLike,
  deleteClubPost,
} from "../../services/clubsApi";
import {
  bodyAfterFirstLine,
  getOptimisticPostLikeState,
  getPostLikeStateFromToggleResponse,
  getImageAttachmentsFromPost,
  resolveClubMediaUrl,
  titleFromPost,
} from "../../features/comments/utils/clubFeedPostUtils.js";
import {
  CLUB_FEED_ACTION_SHADOW,
  clubFeedActionBase,
  clubFeedHeartClass,
  clubFeedNeutralButtonClass,
  clubPostCardActionRowClass,
  clubPostCardArticleClass,
  clubPostCardBodyFeedClass,
  clubPostCardBodyTextClass,
  clubPostCardHeaderClass,
  clubPostCardTitleFeedClass,
  clubPostCardFeedListHoverClass,
} from "./feedPostCardPresentation";
import ClubImageLightbox from "./ClubImageLightbox";
import ClubPostMediaGallery from "./ClubPostMediaGallery";
import FeedPostCardHeader from "./FeedPostCardHeader";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

export function pickViewerId(user) {
  const keys = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.uid,
    user?.sub,
    user?.profile?.id,
    user?.profile?.user_id,
    user?.account?.id,
    user?.account?.user_id,
    user?.account_id,
  ];
  for (const v of keys) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function isModRole(role) {
  const r = String(role || "").toLowerCase();
  return ["owner", "moderator", "mod", "admin"].includes(r);
}

/** Author, club owner, or moderator+ can delete a club feed post (matches backend). */
function canUserDeleteClubPost({ viewerId, post, membership, club }) {
  if (viewerId == null || !post) return false;
  if (Number(post.user_id) === Number(viewerId)) return true;
  const ownerId = club?.owner_user_id ?? club?.ownerUserId;
  if (ownerId != null && Number(ownerId) === Number(viewerId)) return true;
  return isModRole(membership?.role);
}

/**
 * Canonical feed post card (list row): club feed (`ClubFeedPanel`) and community/forum feed (`Community`).
 * Club path: default like/share/delete via clubs API. Community path: pass `onLikeClick` / `onShareClick` / `onDeleteClick`.
 */
export default function FeedPostCard({
  post,
  viewerId,
  accent,
  clubSlug,
  club = null,
  membership = null,
  canInteract,
  canDeleteOverride,
  shareFeedbackId,
  shareFeedbackText,
  onLikeToggled,
  onDeleted,
  onOpenThread,
  onShareFeedback,
  onLikeClick,
  onShareClick,
  onDeleteClick,
  onEditPost,
  replyNavState = { focusComment: true },
  likeNotInteractTitle = "Join this club to like",
  deleteConfirmMessage = "Delete this post?",
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const imageAttachments = useMemo(() => getImageAttachmentsFromPost(post), [post]);
  const attachmentUrls = useMemo(
    () => imageAttachments.map((a) => resolveClubMediaUrl(a.url)).filter(Boolean),
    [imageAttachments]
  );
  const legacyImageUrl = resolveClubMediaUrl(post.image_url);
  const previewImages = useMemo(() => {
    if (attachmentUrls.length) return attachmentUrls;
    return legacyImageUrl ? [legacyImageUrl] : [];
  }, [attachmentUrls, legacyImageUrl]);

  const artImg = resolveClubMediaUrl(post.artwork_image_url);

  const hasMediaOnlyBody =
    !String(post?.content || "").trim() && Boolean(previewImages.length || artImg);

  const canDelete =
    typeof canDeleteOverride === "boolean"
      ? canDeleteOverride
      : canUserDeleteClubPost({ viewerId, post, membership, club });

  const likeCount = Number(post.like_count) || 0;
  const liked = Boolean(post.liked_by_viewer);
  const commentCount = Number(post.comment_count) || 0;

  const postHeading = titleFromPost(post);
  const postBodyRest = bodyAfterFirstLine(post);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewIndex(0);
  }, []);

  const onToggleLike = async (e) => {
    e.stopPropagation();
    if (onLikeClick) {
      await onLikeClick(e);
      return;
    }
    if (!viewerId || !canInteract) return;
    const optimistic = getOptimisticPostLikeState({ liked, likeCount });
    try {
      setLikeBusy(true);
      const rowSrc = post.feed_row_source || "club_post";
      onLikeToggled?.(post.id, optimistic.optimisticLiked, optimistic.optimisticCount, rowSrc);
      const res = await toggleClubPostLike(post.id);
      const { nextLiked, nextCount } = getPostLikeStateFromToggleResponse(res, optimistic);
      onLikeToggled?.(post.id, nextLiked, nextCount, rowSrc);
    } catch {
      onLikeToggled?.(post.id, liked, likeCount, post.feed_row_source || "club_post");
    } finally {
      setLikeBusy(false);
    }
  };

  const onShare = async (e) => {
    e.stopPropagation();
    if (onShareClick) {
      await onShareClick(e);
      return;
    }
    const url = buildClubPostShareUrl(clubSlug, post.id);
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Club thread",
          text: "Check out this club thread on AnimeHouse",
          url,
        });
        onShareFeedback?.(post.id, "Shared");
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      onShareFeedback?.(post.id, "Copied");
    } catch {
      prompt("Copy this link:", url);
    }
  };

  const onDelete = async (e) => {
    e.stopPropagation();
    if (onDeleteClick) {
      if (!window.confirm(deleteConfirmMessage)) return;
      try {
        setDeleteBusy(true);
        await onDeleteClick(e);
      } finally {
        setDeleteBusy(false);
      }
      return;
    }
    if (!canDelete || deleteBusy) return;
    if (!window.confirm(deleteConfirmMessage)) return;
    try {
      setDeleteBusy(true);
      await deleteClubPost(post.id);
      onDeleted?.(post.id, post.feed_row_source || "club_post");
    } catch {
      /* ignore */
    } finally {
      setDeleteBusy(false);
    }
  };

  const openThread = (navState = undefined) => {
    onOpenThread?.(post, navState);
  };

  const heartTokens = clubFeedHeartClass(accent, liked);
  const neutralRowBtn = clubFeedNeutralButtonClass();

  return (
    <article
      className={cn(clubPostCardArticleClass, clubPostCardFeedListHoverClass)}
      onClick={() => openThread()}
    >
      <div className={clubPostCardHeaderClass}>
        <FeedPostCardHeader
          post={post}
          club={club}
          clubSlug={clubSlug}
          variant="feed"
          onShare={onShare}
          onDelete={onDelete}
          canDelete={canDelete}
          deleteBusy={deleteBusy}
          onEditPost={onEditPost}
        />
      </div>

      <div className={cn(clubPostCardBodyFeedClass, "cursor-pointer text-left")}>
        {hasMediaOnlyBody ? (
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
            Media post
          </p>
        ) : null}
        <h3 className={clubPostCardTitleFeedClass}>{postHeading}</h3>
        {postBodyRest ? (
          <div className={cn("mt-3 line-clamp-4", clubPostCardBodyTextClass)}>{postBodyRest}</div>
        ) : null}
        {!String(post.content || "").trim() && (previewImages.length || artImg) ? (
          <p className="mt-3 text-sm text-gray-500 leading-relaxed border-l-2 border-emerald-400/25 pl-4 max-w-prose">
            Media-only post — tap to view thread.
          </p>
        ) : null}

        {previewImages.length ? (
          <ClubPostMediaGallery
            className="mt-5"
            images={previewImages}
            variant="feed"
            onOpenAt={(idx) => {
              setPreviewIndex(idx);
              setPreviewOpen(true);
            }}
          />
        ) : null}

        {post.artwork_id && artImg ? (
          <div className="mt-4" onClick={(e) => e.stopPropagation()}>
            <Link
              to={`/arthouse/art/${post.artwork_id}`}
              className="flex min-w-0 w-full max-w-full gap-3 sm:gap-4 rounded-2xl border border-white/12 bg-black/30 p-3 sm:p-4 hover:border-white/20 hover:bg-black/35 transition-colors items-center"
            >
              <div className="h-[72px] w-[72px] rounded-xl overflow-hidden border border-white/10 shrink-0 bg-white/5">
                <img src={artImg} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0 flex flex-col justify-center">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Linked artwork
                </div>
                <div className="mt-1 text-sm font-semibold text-white truncate">
                  {post.artwork_title || "View artwork"}
                </div>
              </div>
            </Link>
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          clubPostCardActionRowClass,
          "flex flex-nowrap items-center gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch]"
        )}
      >
        <button
          type="button"
          onClick={onToggleLike}
          disabled={likeBusy || !viewerId || !canInteract}
          aria-pressed={liked}
          aria-label={liked ? `Unlike, ${likeCount} likes` : `Like, currently ${likeCount} likes`}
          title={
            !viewerId ? "Log in to like" : !canInteract ? likeNotInteractTitle : liked ? "Unlike" : "Like"
          }
          className={cn(
            clubFeedActionBase,
            heartTokens,
            CLUB_FEED_ACTION_SHADOW,
            !viewerId || !canInteract ? "opacity-50 cursor-not-allowed" : ""
          )}
        >
          <Heart
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              liked ? "fill-current text-emerald-300" : "text-white/70"
            )}
          />
          <span className="tabular-nums">{likeCount}</span>
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openThread(replyNavState && Object.keys(replyNavState).length ? replyNavState : undefined);
          }}
          title="Open full thread"
          aria-label={`Open comments, ${commentCount} comments`}
          className={neutralRowBtn}
        >
          <MessageCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="tabular-nums">{commentCount}</span>
        </button>

        <button type="button" onClick={onShare} title="Share or copy link" className={neutralRowBtn}>
          <Share2 className="h-3.5 w-3.5 shrink-0" />
          Share
        </button>

        {shareFeedbackId === post.id && shareFeedbackText ? (
          <span className="text-[11px] text-emerald-300/90 shrink-0">{shareFeedbackText}</span>
        ) : null}
      </div>

      <ClubImageLightbox
        isOpen={previewOpen}
        images={previewImages}
        currentIndex={previewIndex}
        onClose={closePreview}
        onIndexChange={setPreviewIndex}
      />
    </article>
  );
}
